import { useCallback, useMemo, useState } from "react";

import { API_ACTIVATION_USER, API_RESET_USER_PASSWORD, API_SEARCH, API_USER, API_USERS, BASE_ADMIN } from "../constants/api";
import { EditUser, UserDBType, UsersListType, UserRole } from "../types/user";
import useRequest from "./useRequest";
import { OrderType } from "../types/pagination";

const DEFAULT_LIMIT = 10;

const sortUsers = (data: UserDBType[], target: string, order: 'DESC'|'ASC') => {
  const sortOrder = order === 'DESC' ? -1 : 1;
  return [...data].sort((a, b) => {
    if (target === 'firstName') {
      return a.firstName.localeCompare(b.firstName) * sortOrder;
    }
    if (target === 'lastName') {
      return a.lastName.localeCompare(b.lastName) * sortOrder;
    }
    if (target === 'email') {
      return a.email.localeCompare(b.email) * sortOrder;
    }
    if (target === 'createdAt') {
      const dateA = a.createdAt.toLocaleString() ?? '';
      const dateB = b.createdAt.toLocaleString() ?? '';
      return dateA.localeCompare(dateB) * sortOrder;
    }
    return 0;
  })
}

export interface UsersListOptions {
  limit?: number;
  orderby?: string;
  order?: 'ASC' | 'DESC';
  role?: UserRole | 'all';
}

export const useUsersList = (options?: UsersListOptions) => {
  const { response: users, request, isLoading } = useRequest<UsersListType>();
  
  const limit = options?.limit ?? DEFAULT_LIMIT;
  const defaultOrderby = options?.orderby ?? "createdAt";
  const defaultOrder = options?.order ?? "DESC";
  
  const [sorted, setSorted] = useState<null | {target: string; value: OrderType}>(null);
  const sortedUsers = useMemo(() => {
    const data = users?.data;
    if (!data) return null;
    if (!sorted || sorted.value === 'none') return data;

    return sortUsers(data, sorted.target, sorted.value);
  }, [users?.data, sorted]);

  const fetchUsers = useCallback((page: number = 1, customOptions?: UsersListOptions) => {
    const searchParams: Record<string, string> = {
      page: page.toString(),
      limit: (customOptions?.limit ?? limit).toString(),
      order: customOptions?.order ?? defaultOrder,
      orderby: customOptions?.orderby ?? defaultOrderby
    };

    // Add role filter if specified and not 'all'
    if (customOptions?.role && customOptions.role !== 'all') {
      searchParams.role = customOptions.role;
    } else if (options?.role && options.role !== 'all') {
      searchParams.role = options.role;
    }

    request(BASE_ADMIN, API_USERS, {
      secure: true,
      searchParams
    });
  }, [request, limit, defaultOrder, defaultOrderby, options?.role]);

  const sort = useCallback((target: string, value: OrderType) => {
    setSorted({target, value});
  }, [])

  const search = useCallback((term: string, page: number = 1, customOptions?: UsersListOptions) => {
    const searchParams: Record<string, string> = {
      term,
      page: page.toString(),
      limit: (customOptions?.limit ?? limit).toString(),
      order: customOptions?.order ?? defaultOrder,
      orderby: customOptions?.orderby ?? defaultOrderby
    };

    // Add role filter if specified and not 'all'
    if (customOptions?.role && customOptions.role !== 'all') {
      searchParams.role = customOptions.role;
    } else if (options?.role && options.role !== 'all') {
      searchParams.role = options.role;
    }

    request(BASE_ADMIN, `${API_USERS}/${API_SEARCH}`, {
      secure: true,
      searchParams
    })
  }, [request, limit, defaultOrder, defaultOrderby, options?.role])

  return {
    users: sortedUsers,
    metaData: {
      totalItems: users?.totalItems ?? 0,
      totalPages: users?.totalPages ?? 0,
      page: users?.page ?? 0,
      limit: users?.limit ?? 0,
      nextPage: users?.nextPage ?? 0,
      prevPage: users?.prevPage ?? 0,
    },
    sorted: sorted?.target,
    isLoading,
    fetchUsers,
    sort,
    search
  }
}

export const useSaveUser = () => {
  const { response: saved, error, request: saveRequest } = useRequest<{
    action: "ADD" | "UPDATE" | "UDATE";
    user: UserDBType;
    message?: string;
  }>();

  const saveUser = useCallback((action: 'ADD' | 'UPDATE', user: EditUser, id?: number) => {
    const method = action === "ADD" ? "POST" : "PUT";
    const savePath = API_USER + (action === "UPDATE" && id ? `/${id}` : "");
    saveRequest(BASE_ADMIN, savePath, {method, secure: true}, user);
  }, [saveRequest])

  return {saved, error, saveUser}
}

export const useDeleteUser = () => {
  const {request, response} = useRequest<{message: string}>();

  const deleteUser = (id: number) => {
    request(BASE_ADMIN, `${API_USER}/${id}`, {secure: true, method: 'DELETE'})
  }

  return {deleteUser, response}
}

export const useActivateUser = () => {
  const {request, error, isLoading, response} = useRequest<{message: string}>();

  const activateUser = useCallback((email: string) => {
    request(BASE_ADMIN, `${API_USER}/${API_ACTIVATION_USER}`, {secure: true, method: 'POST'}, {email});
  }, [request])

  return { activateUser, error, isLoading, response }
}

export const useAdminResetUserPassword = () => {
  const {request, error, isLoading, response} = useRequest<{message: string}>();

  const resetUserPassword = useCallback((userId: number, password: string) => {
    request(
      BASE_ADMIN,
      `${API_USER}/${userId}/${API_RESET_USER_PASSWORD}`,
      { secure: true, method: 'POST' },
      { password }
    );
  }, [request])

  return { resetUserPassword, error, isLoading, response }
}

export const useToggleUserStatus = () => {
  const {request, error, isLoading, response} = useRequest<{message: string; user: UserDBType}>();

  const toggleUserStatus = useCallback((userId: number, active: boolean) => {
    request(BASE_ADMIN, `${API_USER}/${userId}/status`, {
      secure: true, 
      method: 'PATCH'
    }, { active });
  }, [request])

  return { toggleUserStatus, error, isLoading, response }
}